import React, { useState, useEffect } from 'react';
import { FileText, BarChart3, PieChart, TrendingUp, Users, Calendar, Clock } from 'lucide-react';
import { getJobCards } from '../../../../models/externalModel';

const MMSReports = () => {
  const [jobData, setJobData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState({});

  useEffect(() => {
    fetchReportData();
  }, []);

  const fetchReportData = async () => {
    try {
      const jobs = await getJobCards({ limit: 100 });
      const jobsList = Array.isArray(jobs) ? jobs : jobs?.data || [];
      setJobData(jobsList);
      setAnalytics(calculateAnalytics(jobsList));
    } catch (error) {
      console.error('Error fetching report data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateAnalytics = (jobs) => {
    const statusCounts = { active: 0, completed: 0, unassigned: 0 };
    const workerStats = {};
    const jobTypes = {};
    
    jobs.forEach(job => {
      // Status distribution
      if (job.OM_STATUS === '6') statusCounts.completed++;
      else if (job.ASSIGNED_TO_NAME && job.ASSIGNED_TO_NAME.trim()) statusCounts.active++;
      else statusCounts.unassigned++;
      
      // Worker performance
      if (job.ASSIGNED_TO_NAME && job.ASSIGNED_TO_NAME.trim() !== ' ') {
        if (!workerStats[job.ASSIGNED_TO_NAME]) {
          workerStats[job.ASSIGNED_TO_NAME] = { total: 0, completed: 0 };
        }
        workerStats[job.ASSIGNED_TO_NAME].total++;
        if (job.OM_STATUS === '6') workerStats[job.ASSIGNED_TO_NAME].completed++;
      }
      
      // Job type distribution
      const type = job.JOB_DESCRIPTION?.split(' ')[0] || 'Other';
      jobTypes[type] = (jobTypes[type] || 0) + 1;
    });
    
    return { statusCounts, workerStats, jobTypes, totalJobs: jobs.length };
  };

  const StatusChart = ({ data }) => {
    const total = data.active + data.completed + data.unassigned;
    const activePercent = (data.active / total) * 100;
    const completedPercent = (data.completed / total) * 100;
    const unassignedPercent = (data.unassigned / total) * 100;
    
    return (
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <span className="text-sm font-medium">Active</span>
          <span className="text-sm text-gray-600">{data.active} ({activePercent.toFixed(1)}%)</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${activePercent}%` }}></div>
        </div>
        
        <div className="flex justify-between items-center">
          <span className="text-sm font-medium">Completed</span>
          <span className="text-sm text-gray-600">{data.completed} ({completedPercent.toFixed(1)}%)</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div className="bg-green-500 h-2 rounded-full" style={{ width: `${completedPercent}%` }}></div>
        </div>
        
        <div className="flex justify-between items-center">
          <span className="text-sm font-medium">Unassigned</span>
          <span className="text-sm text-gray-600">{data.unassigned} ({unassignedPercent.toFixed(1)}%)</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div className="bg-orange-500 h-2 rounded-full" style={{ width: `${unassignedPercent}%` }}></div>
        </div>
      </div>
    );
  };

  const WorkerPerformance = ({ workers }) => {
    const topWorkers = Object.entries(workers)
      .map(([name, stats]) => ({ name, ...stats, rate: (stats.completed / stats.total) * 100 }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 5);
    
    return (
      <div className="space-y-3">
        {topWorkers.map((worker, i) => (
          <div key={i} className="flex justify-between items-center p-3 bg-gray-50 rounded">
            <div>
              <div className="font-medium text-sm">{worker.name}</div>
              <div className="text-xs text-gray-500">{worker.total} jobs • {worker.rate.toFixed(1)}% completion</div>
            </div>
            <div className="text-right">
              <div className="text-sm font-medium text-green-600">{worker.completed}</div>
              <div className="text-xs text-gray-500">completed</div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container mx-auto px-4 py-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-start">
            <div className="flex-shrink-0 rounded-lg p-2.5 mr-3 mt-0.5" style={{ backgroundColor: 'rgba(0, 135, 212, 0.15)' }}>
              <BarChart3 className="h-5 w-5" style={{ color: '#0087D4' }} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800">MMS Reports & Analytics</h1>
              <p className="text-xs text-gray-500 mb-1">
                Performance insights and operational metrics
              </p>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center">
              <FileText className="h-8 w-8 text-blue-500 mr-3" />
              <div>
                <p className="text-2xl font-bold">{analytics.totalJobs}</p>
                <p className="text-sm text-gray-600">Total Jobs</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center">
              <Clock className="h-8 w-8 text-orange-500 mr-3" />
              <div>
                <p className="text-2xl font-bold">{analytics.statusCounts?.active || 0}</p>
                <p className="text-sm text-gray-600">Active Jobs</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center">
              <TrendingUp className="h-8 w-8 text-green-500 mr-3" />
              <div>
                <p className="text-2xl font-bold">{analytics.statusCounts?.completed || 0}</p>
                <p className="text-sm text-gray-600">Completed</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center">
              <Users className="h-8 w-8 text-purple-500 mr-3" />
              <div>
                <p className="text-2xl font-bold">{Object.keys(analytics.workerStats || {}).length}</p>
                <p className="text-sm text-gray-600">Active Workers</p>
              </div>
            </div>
          </div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center mb-4">
              <PieChart className="h-5 w-5 text-blue-500 mr-2" />
              <h2 className="text-lg font-semibold">Job Status Distribution</h2>
            </div>
            {analytics.statusCounts && <StatusChart data={analytics.statusCounts} />}
          </div>
          
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center mb-4">
              <Users className="h-5 w-5 text-purple-500 mr-2" />
              <h2 className="text-lg font-semibold">Top Performing Workers</h2>
            </div>
            {analytics.workerStats && <WorkerPerformance workers={analytics.workerStats} />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MMSReports;
